import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
  List,
  SvgIconProps,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { ExpandLess, ExpandMore, ChevronRight } from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SidebarMenuItem } from "./types/sidebar";
import { useSidebar } from "./SidebarContext";
import type { IThemeState } from "../../types/theme";

interface SidebarItemProps {
  item: SidebarMenuItem;
  level: number;
  parentKey?: string;
  theme: IThemeState;
  onPermissionCheck: (permission?: string) => boolean;
}

type FlatMenuItem = SidebarMenuItem & {
  displayTitle: string;
};

const flattenChildren = (
  items: SidebarMenuItem[],
  onPermissionCheck: (permission?: string) => boolean,
  parentLabel = "",
): FlatMenuItem[] => {
  return items.flatMap((child) => {
    if (!onPermissionCheck(child.permission)) return [];

    const displayTitle = parentLabel
      ? `${parentLabel} / ${child.title}`
      : child.title;

    if (child.children && child.children.length > 0) {
      return flattenChildren(child.children, onPermissionCheck, displayTitle);
    }

    return [
      {
        ...child,
        displayTitle,
      },
    ];
  });
};

const hasPathInTree = (
  items: SidebarMenuItem[] | undefined,
  pathname: string,
): boolean => {
  if (!items || items.length === 0) return false;

  return items.some((child) => {
    if (child.path === pathname) return true;
    return hasPathInTree(child.children, pathname);
  });
};

const hasVisibleChild = (
  items: SidebarMenuItem[],
  onPermissionCheck: (permission?: string) => boolean,
): boolean => {
  return items.some((child) => {
    if (!onPermissionCheck(child.permission)) return false;
    if (child.children?.length) return hasVisibleChild(child.children, onPermissionCheck);
    return true;
  });
};

export const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  level,
  parentKey,
  theme,
  onPermissionCheck,
}) => {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode, theme);
  const { isCollapsed, expandedMenus, toggleMenu } = useSidebar();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isComingSoon = !hasChildren && !item.path && !item.disabled;
  const isDisabled = item.disabled || isComingSoon;

  const collapsedMenuItems = useMemo(() => {
    if (!hasChildren) return [];
    return flattenChildren(item.children!, onPermissionCheck);
  }, [hasChildren, item.children, onPermissionCheck]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) return;

    if (hasChildren) {
      if (isCollapsed) {
        setMenuAnchorEl(event.currentTarget);
      } else {
        toggleMenu(itemKey);
      }
    }
  };

  const [notificationCount, setNotificationCount] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (item.path === "/endpoint-requests") {
      const updateCount = () => {
        const count = parseInt(
          localStorage.getItem("endpoint_requests_notification_count") || "0",
          10,
        );
        setNotificationCount(count);
      };

      updateCount();
      window.addEventListener("endpoint-requests-updated", updateCount);

      return () => {
        window.removeEventListener("endpoint-requests-updated", updateCount);
      };
    }
  }, [item.path]);

  if (!onPermissionCheck(item.permission)) return null;
  if (hasChildren && !hasVisibleChild(item.children!, onPermissionCheck)) return null;

  const itemKey = parentKey ? `${parentKey}-${item.title}` : item.title;

  const isExpanded = expandedMenus[itemKey] || false;
  const isSelected = pathname === item.path;
  const hasSelectedChild = hasPathInTree(item.children, pathname);
  const isActive = isSelected || hasSelectedChild;
  const isTopLevel = level === 0;

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const defaultTooltip = isComingSoon ? "Coming soon" : "Access denied";
  const tooltipTitle = isCollapsed
    ? isDisabled
      ? item.tooltip || `${item.title} (${defaultTooltip})`
      : item.title
    : isDisabled
      ? item.tooltip || defaultTooltip
      : "";

  return (
    <Box key={itemKey}>
      <Tooltip title={tooltipTitle} placement="right" arrow enterDelay={250}>
        <ListItem disablePadding sx={{ opacity: isDisabled ? 0.5 : 1 }}>
          <ListItemButton
            component={
              !hasChildren && item.path && !isDisabled ? Link : "div"
            }
            to={
              !hasChildren && item.path && !isDisabled
                ? item.path
                : undefined
            }
            onClick={handleClick}
            disabled={isDisabled}
            selected={isActive && !hasChildren ? true : isActive}
            sx={{
              pl: isTopLevel ? 3 : 3 + level * 2,
              pr: 2,
              minHeight: 50,
              borderRadius: 1.5,
              transition: "all 0.2s ease",
              backgroundColor: isActive
                ? colors.neutral[800]
                : "transparent",
              color: isActive
                ? colors.primary[400]
                : colors.neutral[100],
              cursor: isDisabled ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: isDisabled
                  ? "transparent"
                  : colors.neutral[800] || "rgba(0,0,0,0.08)",
                borderRadius: 1.5,
              },
              "&.Mui-selected": {
                backgroundColor: colors.neutral[800],
                color: colors.primary[400],
                borderLeft: `3px solid ${colors.primary[400]}`,
              },
              "&.Mui-disabled": {
                color: colors.neutral[400],
              },
            }}
          >
            {item.icon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: isCollapsed ? 0 : 2,
                  minWidth: 24,
                }}
              >
                {item.path === "/endpoint-requests" && notificationCount > 0 ? (
                  <Badge
                    badgeContent={notificationCount}
                    color="error"
                    max={99}
                  >
                    {React.cloneElement(
                      item.icon as React.ReactElement<SvgIconProps>,
                      {
                        sx: {
                          fontSize: "20px",
                          color: isActive
                            ? colors.primary[400]
                            : colors.neutral[100],
                        },
                      },
                    )}
                  </Badge>
                ) : (
                  React.cloneElement(
                    item.icon as React.ReactElement<SvgIconProps>,
                    {
                      sx: {
                        fontSize: "20px",
                        color: isActive
                          ? colors.primary[400]
                          : colors.neutral[100],
                      },
                    },
                  )
                )}
              </Box>
            )}

            {!isCollapsed && (
              <ListItemText
                primary={item.title}
                sx={{
                  "& .MuiTypography-root": {
                    fontWeight: isActive ? 600 : 500,
                    fontSize: "0.95rem",
                    color: "inherit",
                  },
                }}
              />
            )}

            {hasChildren && !isCollapsed && (
              <IconButton
                size="small"
                sx={{
                  transition: "transform 0.2s ease",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  color: "inherit",
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}

            {hasChildren && isCollapsed && (
              <ChevronRight
                sx={{
                  fontSize: 18,
                  ml: 0.5,
                  color: isActive
                    ? colors.primary[400]
                    : colors.neutral[100],
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>

      {hasChildren && !isCollapsed && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children!.map((child) => (
              <SidebarItem
                key={`${itemKey}-${child.title}`}
                item={child}
                level={level + 1}
                parentKey={itemKey}
                theme={theme}
                onPermissionCheck={onPermissionCheck}
              />
            ))}
          </List>
        </Collapse>
      )}

      {hasChildren && isCollapsed && (
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              minWidth: 260,
              maxWidth: 340,
              backgroundColor: colors.neutral[900],
              color: colors.neutral[100],
              border: `1px solid ${colors.neutral[700]}`,
              mt: 0.5,
            },
          }}
        >
          {collapsedMenuItems.map((child) => {
            const childSelected = pathname === child.path;
            const childComingSoon = !child.path && !child.disabled;
            const childDisabled = child.disabled || childComingSoon;

            return (
              <Tooltip
                key={`${itemKey}-${child.displayTitle}`}
                title={
                  childDisabled
                    ? child.tooltip ||
                      (childComingSoon ? "Coming soon" : "Access denied")
                    : ""
                }
                placement="right"
                arrow
              >
                <Box>
                  <MenuItem
                    disabled={childDisabled}
                    onClick={() => {
                      if (child.path && !childDisabled) {
                        navigate(child.path);
                        handleCloseMenu();
                      }
                    }}
                    selected={childSelected}
                    sx={{
                      opacity: childDisabled ? 0.5 : 1,
                      py: 1,
                      px: 1.5,
                      whiteSpace: "normal",
                      alignItems: "flex-start",
                      backgroundColor: childSelected
                        ? colors.neutral[800]
                        : "transparent",
                      color: childSelected
                        ? colors.primary[400]
                        : colors.neutral[100],
                      "&.Mui-selected": {
                        backgroundColor: colors.neutral[800],
                        color: colors.primary[400],
                      },
                    }}
                  >
                    {child.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: "inherit",
                          mt: 0.2,
                        }}
                      >
                        {React.cloneElement(
                          child.icon as React.ReactElement<SvgIconProps>,
                          {
                            sx: {
                              fontSize: "18px",
                              color: "inherit",
                            },
                          },
                        )}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={child.displayTitle}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "0.9rem",
                          fontWeight: childSelected ? 600 : 500,
                          whiteSpace: "normal",
                          lineHeight: 1.3,
                        },
                      }}
                    />
                  </MenuItem>
                </Box>
              </Tooltip>
            );
          })}
        </Menu>
      )}
    </Box>
  );
};
